/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * organizationIndexedDBUtils — cache cơ cấu tổ chức (IndexedDB) + sync quyền login.
 *
 * Version (tránh 400 Path: $.Version):
 * - asOrgVersion / getVersionFieldName — wrap string (cached-data) → VersionDTO khi save*
 * - getOrgVersions — chỉ trả object (bỏ string legacy); getStoredOrgVersion so sánh hash
 *
 * Sync profile:
 * - syncLoggedInUserPermissionsAfterRoleGroupChange — chỉ getProfile+setProfile (không getRest)
 *
 * Org sync khác: saveEmployees/Departments/Positions/RestToIndexedDB, fetchInitDataOrganization,
 * scheduleBatchSync, updateOrganizationVersion, …
 */
import { organizationApi } from '@/services/organization/organization-api';
import { getRecordById, putRecord, openDB } from '@/services/indexedDB/indexed-db-service';
import { useAuthStore } from '@/store/useAuthStore';
import { getPhanHeFromStorage } from '@/utils/phanHeStorage';
import CryptoService from './cryptoService';
import { getOrgSyncBroadcast } from './orgSyncBroadcast';
import type {
    OrganizationData,
    OrganizationEntityType,
    OrgRawResponse,
    OrgDepartment,
    OrgEmployee,
    OrgPosition,
    OrgUnit,
    OrgRole,
    OrgPeriod,
    OrgPeriodDetail,
    OrgPerspective,
    OrgVersion,
} from '@/types/organizationIndexedDB';
import {
    ORG_KEY_EMPLOYEES,
    ORG_KEY_DEPARTMENTS,
    ORG_KEY_POSITIONS,
    ORG_KEY_REST,
} from '@/types/organizationIndexedDB';
export type { OrganizationData, OrgRawResponse } from '@/types/organizationIndexedDB';

// Lock để tránh race condition
let isSaving = false;

/**
 * Đã fetch full org (4 API) thành công trong lần load page này chưa.
 * Biến module → mất khi reload page (sync lại), giữ nguyên khi đổi route SPA.
 * Tránh cache "không đủ" (vd. tenant chưa có chức vụ/perspective) bị fetch lại mỗi lần đổi route.
 */
let _orgInitSyncedThisLoad = false;
export function hasOrgInitSyncedThisLoad(): boolean {
    return _orgInitSyncedThisLoad;
}

/**
 * Chuẩn hóa dữ liệu raw từ backend (camelCase) thành OrganizationData (PascalCase).
 */
export function normalizeOrganizationJson(
    rawJson: OrgRawResponse | null | undefined,
    version?: string | null
): OrganizationData {
    const departmentsDict = rawJson?.departments ?? {};
    const employeesDict = rawJson?.employees ?? {};
    const positionsDict = rawJson?.positions ?? {};
    const unitsDict = rawJson?.units ?? {};
    const rolesDict = rawJson?.roles ?? {};
    const periodDict = rawJson?.period ?? {};
    const perspectivesDict = rawJson?.perspectives ?? {};

    let periodArray: any[] = [];
    if (Array.isArray(periodDict)) periodArray = periodDict;
    else if (typeof periodDict === 'object' && periodDict !== null) periodArray = Object.values(periodDict);

    const period: OrgPeriod[] = periodArray
        .map((p: any) => {
            if (!p) return null;
            return {
                ID: p.ID ?? p.id ?? '',
                Ten: p.Ten ?? p.ten,
                BatDau: p.BatDau ?? p.batDau,
                KetThuc: p.KetThuc ?? p.ketThuc,
                NamTaiChinh: p.NamTaiChinh ?? p.namTaiChinh,
                Site: p.Site ?? p.site,
                PeriodDetails: (Array.isArray(p.PeriodDetails ?? p.periodDetails)
                    ? (p.PeriodDetails ?? p.periodDetails)
                    : []
                ).map((pd: any): OrgPeriodDetail => ({
                    ID: pd.ID ?? pd.id ?? '',
                    NamBSCID: pd.NamBSCID ?? pd.namBSCID,
                    Year: pd.Year ?? pd.year,
                    BatDau: pd.BatDau ?? pd.batDau,
                    KetThuc: pd.KetThuc ?? pd.ketThuc,
                    Site: pd.Site ?? pd.site,
                })),
            };
        })
        .filter(Boolean) as OrgPeriod[];

    const departments: OrgDepartment[] = Object.keys(departmentsDict).map((key) => {
        const d = departmentsDict[key] as any;
        const id = d?.ID ?? d?.id ?? key;
        const parentID = d?.ParentID ?? d?.parentID ?? '0';
        const ten = d?.Ten ?? d?.ten ?? '';
        const viTri = d?.ViTri ?? d?.viTri ?? 0;
        const treePath = d?.TreePath ?? d?.treePath ?? '';
        const keyOrg = d?.Key ?? d?.key ?? key;
        return {
            ID: id,
            Ten: ten,
            ParentID: parentID,
            ViTri: viTri,
            TreePath: treePath,
            Key: keyOrg,
            IsCompany: d?.IsCompany ?? d?.isCompany ?? keyOrg === 1,
            Prefix: d?.Prefix ?? d?.prefix ?? '',
        };
    });

    const toEmployee = (e: any, key: string): OrgEmployee => ({
        ID: e?.ID ?? e?.id ?? key,
        TenNhanVien: e?.TenNhanVien ?? e?.tenNhanVien ?? '',
        MaNhanVien: e?.MaNhanVien ?? e?.maNhanVien ?? '',
        BPID: e?.BPID ?? e?.bpid ?? '',
        CVID: e?.CVID ?? e?.cvid ?? '',
        Email: e?.Email ?? e?.email,
        Prefix: e?.Prefix ?? e?.prefix,
        Hinh: e?.Hinh ?? e?.hinh,
        GioiTinh: e?.GioiTinh ?? e?.gioiTinh,
        NghiViec: e?.NghiViec ?? e?.nghiViec ?? false,
        PhanHe: e?.PhanHe ?? e?.phanHe,
        IsQuanLy: e?.IsQuanLy ?? e?.isQuanLy ?? false,
        Parent: e?.Parent ?? e?.parent ?? '',
    });

    const employees: OrgEmployee[] = Object.keys(employeesDict).map((key) =>
        toEmployee(employeesDict[key], key)
    );

    const positions: OrgPosition[] = Object.keys(positionsDict).map((key) => {
        const p = positionsDict[key] as any;
        return {
            ID: p?.ID ?? p?.id ?? key,
            Ten: p?.Ten ?? p?.ten ?? '',
            ParentID: p?.ParentID ?? p?.parentID ?? '0',
            ViTri: p?.ViTri ?? p?.viTri ?? 0,
        };
    });

    const units: OrgUnit[] = Object.keys(unitsDict)
        .map((key) => {
            const u = unitsDict[key] as any;
            const rawChilds = u?.Childs ?? u?.childs ?? [];
            return {
                ID: u?.ID ?? u?.id ?? key,
                Ten: u?.Ten ?? u?.ten ?? '',
                Childs: (Array.isArray(rawChilds) ? rawChilds : []).map((c: any) => {
                    const ten = c?.Ten ?? c?.ten ?? '';
                    const tenNgan =
                        c?.tenNgan ?? c?.TenNgan ?? ten ?? c?.TenNgan ?? c?.ten ?? '';

                    return {
                        ...c,
                        // Normalize id fields
                        ID: c?.ID ?? c?.id ?? '',
                        id: c?.id ?? c?.ID ?? '',
                        // Normalize name fields
                        Ten: c?.Ten ?? c?.ten ?? ten,
                        ten: c?.ten ?? c?.Ten ?? ten,
                        // Normalize short name fields used by backend validations
                        TenNgan: c?.TenNgan ?? c?.tenNgan ?? tenNgan,
                        tenNgan: c?.tenNgan ?? c?.TenNgan ?? tenNgan,
                        // Normalize giaTri fields
                        GiaTri: c?.GiaTri ?? c?.giaTri ?? c?.giaTri ?? 0,
                        giaTri: c?.giaTri ?? c?.GiaTri ?? 0,
                        // Preserve site fields (used when saving)
                        Site: c?.Site ?? c?.site ?? '',
                        site: c?.site ?? c?.Site ?? '',
                        // Preserve parent unit linkage if present
                        IDDoLuong: c?.IDDoLuong ?? c?.idDoLuong ?? '',
                        idDoLuong: c?.idDoLuong ?? c?.IDDoLuong ?? '',
                    };
                }),
                IsDefault: u?.IsDefault ?? u?.isDefault ?? false,
                hinhThucTinh: u?.hinhThucTinh ?? u?.HinhThucTinh ?? null,
            };
        })
        .sort((a, b) => (a.IsDefault && !b.IsDefault ? -1 : !a.IsDefault && b.IsDefault ? 1 : 0));

    const roles: OrgRole[] = Object.keys(rolesDict).map((key) => {
        const r = rolesDict[key] as any;
        return {
            ID: r?.ID ?? r?.id ?? key,
            Ten: r?.Ten ?? r?.ten ?? '',
            FullControl: r?.FullControl ?? r?.fullControl ?? false,
            QuanLy: r?.QuanLy ?? r?.quanLy ?? false,
            SiteModule: r?.SiteModule ?? r?.siteModule ?? 0,
        };
    });

    const perspectives: OrgPerspective[] = Object.keys(perspectivesDict).map((key) => {
        const p = perspectivesDict[key] as any;
        return {
            ID: p?.ID ?? p?.id ?? key,
            Ten: p?.Ten ?? p?.ten,
            MauNen: p?.MauNen ?? p?.mauNen ?? '',
            MauChu: p?.MauChu ?? p?.mauChu ?? '',
            MauSpan: p?.MauSpan ?? p?.mauSpan ?? '',
            Site: p?.Site ?? p?.site ?? '',
        };
    });

    const indexedDBData: OrganizationData = {
        id: 'merged',
        employees,
        departments,
        positions,
        units,
        roles,
        perspectives,
        period,
        updatedAt: Date.now(),
        version: version ?? null,
    };
    return indexedDBData;
}

async function ensureDatabase() {
    const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('openDB timeout')), 5000)
    );

    try {
        const db = await Promise.race([
            openDB('TOPPION_ORG_DB', 1, [{ name: 'organization', keyPath: 'id' }]),
            timeout
        ]);
        db.close();
    } catch (error) {
        console.error('Error initializing database:', error);
    }
}

/** Record lưu từng slice trong IndexedDB (id = key). */
interface EmployeesSlice {
    id: typeof ORG_KEY_EMPLOYEES;
    employees: OrgEmployee[];
    version?: OrgVersion | null;
    updatedAt: number;
}
interface DepartmentsSlice {
    id: typeof ORG_KEY_DEPARTMENTS;
    departments: OrgDepartment[];
    version?: OrgVersion | null;
    updatedAt: number;
}
interface PositionsSlice {
    id: typeof ORG_KEY_POSITIONS;
    positions: OrgPosition[];
    version?: OrgVersion | null;
    updatedAt: number;
}
interface RestSlice {
    id: typeof ORG_KEY_REST;
    units: OrgUnit[];
    roles: OrgRole[];
    period: OrgPeriod[];
    perspectives: OrgPerspective[];
    version?: OrgVersion | null;
    updatedAt: number;
}

async function getEmployeesSlice(): Promise<EmployeesSlice | null> {
    await ensureDatabase();
    return getRecordById<EmployeesSlice>('organization', ORG_KEY_EMPLOYEES);
}
async function getDepartmentsSlice(): Promise<DepartmentsSlice | null> {
    await ensureDatabase();
    return getRecordById<DepartmentsSlice>('organization', ORG_KEY_DEPARTMENTS);
}
async function getPositionsSlice(): Promise<PositionsSlice | null> {
    await ensureDatabase();
    return getRecordById<PositionsSlice>('organization', ORG_KEY_POSITIONS);
}
async function getRestSlice(): Promise<RestSlice | null> {
    await ensureDatabase();
    return getRecordById<RestSlice>('organization', ORG_KEY_REST);
}

/** Map module → field VersionDTO (dùng so sánh local vs remote + wrap string khi save). */
function getVersionFieldName(module: string): keyof OrgVersion | null {
    switch (module) {
        case 'employees': return 'EmployeeVersion';
        case 'departments': return 'DepartmentVersion';
        case 'positions': return 'PositionVersion';
        case 'rest': return 'UnitVersion';
        default: return null;
    }
}

/**
 * cached-data / SignalR trả `version: string`; BE `RequestDTO.Version` là VersionDTO object.
 * Wrap tại save — nếu lưu string thô, POST get-full-organization sẽ 400 Path: $.Version.
 */
function asOrgVersion(
    module: string,
    version: string | OrgVersion | null | undefined,
): OrgVersion | null {
    if (version == null || version === '') return null;
    if (typeof version === 'object' && !Array.isArray(version)) return version as OrgVersion;
    if (typeof version !== 'string') return null;
    const field = getVersionFieldName(module);
    return field ? ({ [field]: version } as OrgVersion) : null;
}

/** Lấy version object đã lưu (bỏ qua string legacy trong IDB). */
export async function getOrgVersions(): Promise<OrgVersion | null> {
    try {
        for (const slice of [
            await getEmployeesSlice(),
            await getDepartmentsSlice(),
            await getPositionsSlice(),
            await getRestSlice(),
        ]) {
            const v = slice?.version as OrgVersion | string | null | undefined;
            if (v && typeof v === 'object') return v;
        }
        return null;
    } catch {
        return null;
    }
}

/**
 * Lấy version string của 1 module (so sánh với remote trước khi fetch).
 */
export async function getStoredOrgVersion(module: string): Promise<string | null> {
    try {
        const field = getVersionFieldName(module);
        if (!field) return null;

        let slice: { version?: OrgVersion | string | null } | null = null;
        switch (module) {
            case 'employees': slice = await getEmployeesSlice(); break;
            case 'departments': slice = await getDepartmentsSlice(); break;
            case 'positions': slice = await getPositionsSlice(); break;
            case 'rest': slice = await getRestSlice(); break;
        }
        const ver = slice?.version;
        if (typeof ver === 'string') return ver || null;
        return ver?.[field] ?? null;
    } catch {
        return null;
    }
}

/**
 * Đọc 4 key và gộp thành OrganizationData (tương thích code cũ).
 */
export async function getOrganizationIndexedDB(): Promise<OrganizationData | null> {
    try {
        const [emp, dept, pos, rest] = await Promise.all([
            getEmployeesSlice(),
            getDepartmentsSlice(),
            getPositionsSlice(),
            getRestSlice(),
        ]);
        const hasAny = emp || dept || pos || rest;
        if (!hasAny) return null;

        const merged: OrganizationData = {
            id: 'merged',
            employees: emp?.employees ?? [],
            departments: dept?.departments ?? [],
            positions: pos?.positions ?? [],
            units: rest?.units ?? [],
            roles: rest?.roles ?? [],
            period: rest?.period ?? [],
            perspectives: rest?.perspectives ?? [],
            updatedAt: Math.max(
                emp?.updatedAt ?? 0,
                dept?.updatedAt ?? 0,
                pos?.updatedAt ?? 0,
                rest?.updatedAt ?? 0
            ),
        };
        return merged;
    } catch (error) {
        console.error('Error getting organization data:', error);
        return null;
    }
}

/**
 * Đọc danh sách units từ IndexedDB — không gọi API organization.
 */
export async function readUnitsFromIndexedDB(): Promise<any[]> {
  const data = await getOrganizationIndexedDB();
  if (!data?.units) return [];
  return Array.isArray(data.units) ? data.units : Object.values(data.units);
}

/**
 * Lưu từng slice (dùng sau khi gọi 4 API tách).
 * version có thể là string từ cached-data — asOrgVersion wrap trước khi ghi.
 */
export async function saveEmployeesToIndexedDB(
    raw: { employees?: Record<string, any> },
    version?: OrgVersion | string | null
): Promise<boolean> {
    while (isSaving) await new Promise((r) => setTimeout(r, 10));
    isSaving = true;
    try {
        const norm = normalizeOrganizationJson({ employees: raw.employees ?? {} }, undefined);
        await ensureDatabase();
        await putRecord('organization', {
            id: ORG_KEY_EMPLOYEES,
            employees: norm.employees,
            version: asOrgVersion('employees', version),
            updatedAt: Date.now(),
        });
        return true;
    } finally {
        isSaving = false;
    }
}

export async function saveDepartmentsToIndexedDB(
    raw: { departments?: Record<string, any> },
    version?: OrgVersion | string | null
): Promise<boolean> {
    while (isSaving) await new Promise((r) => setTimeout(r, 10));
    isSaving = true;
    try {
        const norm = normalizeOrganizationJson({ departments: raw.departments ?? {} }, undefined);
        await ensureDatabase();
        await putRecord('organization', {
            id: ORG_KEY_DEPARTMENTS,
            departments: norm.departments,
            version: asOrgVersion('departments', version),
            updatedAt: Date.now(),
        });
        return true;
    } finally {
        isSaving = false;
    }
}

export async function savePositionsToIndexedDB(
    raw: { positions?: Record<string, any> },
    version?: OrgVersion | string | null
): Promise<boolean> {
    while (isSaving) await new Promise((r) => setTimeout(r, 10));
    isSaving = true;
    try {
        const norm = normalizeOrganizationJson({ positions: raw.positions ?? {} }, undefined);
        await ensureDatabase();
        await putRecord('organization', {
            id: ORG_KEY_POSITIONS,
            positions: norm.positions ?? [],
            version: asOrgVersion('positions', version),
            updatedAt: Date.now(),
        });
        return true;
    } finally {
        isSaving = false;
    }
}

export async function saveRestToIndexedDB(
    raw: {
        units?: Record<string, any>;
        roles?: Record<string, any>;
        period?: Record<string, any> | any[];
        perspectives?: Record<string, any>;
    },
    version?: OrgVersion | string | null
): Promise<boolean> {
    while (isSaving) await new Promise((r) => setTimeout(r, 10));
    isSaving = true;
    try {
        const norm = normalizeOrganizationJson(
            {
                units: raw.units ?? {},
                roles: raw.roles ?? {},
                period: raw.period ?? {},
                perspectives: raw.perspectives ?? {},
            },
            undefined
        );
        await ensureDatabase();
        await putRecord('organization', {
            id: ORG_KEY_REST,
            units: norm.units,
            roles: norm.roles,
            period: norm.period,
            perspectives: norm.perspectives ?? [],
            version: asOrgVersion('rest', version),
            updatedAt: Date.now(),
        });
        return true;
    } finally {
        isSaving = false;
    }
}

/**
 * Save organization data (legacy: 1 blob → ghi ra 4 key).
 */
export async function saveOrganizationIndexedDB(data: {
    type: string;
    employees?: any;
    departments?: any;
    positions?: any;
    units?: any;
    roles?: any;
    perspectives?: any;
    period?: any;
    version?: string | OrgVersion;
}): Promise<boolean | any> {
    while (isSaving) await new Promise((r) => setTimeout(r, 10));
    isSaving = true;
    try {
        if (data.type !== 'company') return true;

        const rawJson = {
            employees: data.employees ?? {},
            departments: data.departments ?? {},
            positions: data.positions ?? {},
            units: data.units ?? {},
            roles: data.roles ?? {},
            perspectives: data.perspectives ?? {},
            period: data.period ?? {},
        };
        const norm = normalizeOrganizationJson(rawJson, undefined);
        const ver = data.version as OrgVersion | undefined;
        await ensureDatabase();

        await putRecord('organization', {
            id: ORG_KEY_EMPLOYEES,
            employees: norm.employees,
            version: ver ?? null,
            updatedAt: Date.now(),
        });
        await putRecord('organization', {
            id: ORG_KEY_DEPARTMENTS,
            departments: norm.departments,
            version: ver ?? null,
            updatedAt: Date.now(),
        });
        await putRecord('organization', {
            id: ORG_KEY_POSITIONS,
            positions: norm.positions ?? [],
            version: ver ?? null,
            updatedAt: Date.now(),
        });
        await putRecord('organization', {
            id: ORG_KEY_REST,
            units: norm.units,
            roles: norm.roles,
            period: norm.period,
            perspectives: norm.perspectives ?? [],
            version: ver ?? null,
            updatedAt: Date.now(),
        });
        return true;
    } catch  {
        // noop
    } finally {
        isSaving = false;
    }
    return false;
}

export type OrganizationSyncModule = 'employees' | 'departments' | 'positions' | 'rest';

const SIGNALR_EVENT_MODULE_MAP: Record<string, OrganizationSyncModule> = {
    reloadEmployee: 'employees',
    reloadDepartment: 'departments',
    reloadPosition: 'positions',
    reloadRoleGroup: 'rest',
    reloadUnit: 'rest',
    reloadPeriod: 'rest',
    employees: 'employees',
    departments: 'departments',
    positions: 'positions',
    rest: 'rest',
};

export function resolveOrganizationSyncModule(data?: any): OrganizationSyncModule | null {
    if (data == null) return null;
    const raw = data.module ?? data.Module ?? data.type ?? data.Type ?? data.event ?? data.Event;
    if (typeof raw !== 'string') return null;
    if (SIGNALR_EVENT_MODULE_MAP[raw]) return SIGNALR_EVENT_MODULE_MAP[raw];
    if (raw === 'employees' || raw === 'departments' || raw === 'positions' || raw === 'rest') {
        return raw;
    }
    return null;
}

export function dispatchOrganizationVersionUpdated(module: OrganizationSyncModule | 'all'): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('updateOrganizationVersion', { detail: { module } }));
    window.dispatchEvent(new CustomEvent('indexedDBReloaded', { detail: { module } }));
}

type CoalescingRequest = { promise: Promise<boolean>; status: 'pending' | 'done' };
const _coalescingRequests = new Map<OrganizationSyncModule, CoalescingRequest>();

/**
 * Coalescing singleton — đảm bảo chỉ 1 request API cho N callers đồng thời.
 * Dùng cho SignalR event handlers và version polling.
 * KHÔNG dùng cho user login (login gọi trực tiếp).
 *
 * Multi-tab: trước khi gọi API, dùng BroadcastChannel để đảm bảo chỉ 1 tab (writer)
 * gọi BE + ghi IndexedDB. Các tab khác return true (không error) và đợi `sync-complete`
 * qua channel listener (xem initOrgSyncBroadcastListener dưới).
 */
export async function coalescingSync(module: OrganizationSyncModule): Promise<boolean> {
    const existing = _coalescingRequests.get(module);
    if (existing && existing.status === 'pending') {
        return existing.promise;
    }

    const promise = (async (): Promise<boolean> => {
        // Acquire writer lock: 1 tab duy nhất trong N tab được gọi BE + ghi IndexedDB.
        const broadcast = getOrgSyncBroadcast();
        const isWriter = await broadcast.acquireWriterLock(module);

        if (!isWriter) {
            // Tab khác đang là writer. Đợi `sync-complete` qua channel (timeout 10s
            // an toàn tránh treo UI nếu writer tab bị đóng trước khi broadcast).
            const ok = await waitForSyncComplete(module, 10_000);
            if (ok) {
                // Vẫn dispatch để UI patch nếu tab này là listener đầu tiên nhận event.
                dispatchOrganizationVersionUpdated(module);
            }
            return ok;
        }

        // Mình là writer → gọi API + ghi IndexedDB.
        try {
            const result = await syncOrganizationModuleFromApi(module);
            // Lấy version mới nhất vừa ghi để broadcast cho listener patch UI.
            const newVersion = await getModuleVersion(module);
            broadcast.broadcastSyncComplete(module, newVersion);
            return result;
        } catch (e) {
            // Writer thất bại → nhả lock để tab khác có thể retry.
            broadcast.broadcastSyncComplete(module, null);
            throw e;
        }
    })();

    _coalescingRequests.set(module, { promise, status: 'pending' });

    try {
        const result = await promise;
        _coalescingRequests.set(module, { promise: Promise.resolve(result), status: 'done' });
        // Xóa sau 2s để allow next batch
        setTimeout(() => _coalescingRequests.delete(module), 2000);
        return result;
    } catch (e) {
        _coalescingRequests.delete(module);
        throw e;
    }
}

/** Lấy version string của 1 module từ IndexedDB (dùng để broadcast cho listener). */
async function getModuleVersion(module: OrganizationSyncModule): Promise<string | null> {
    try {
        return (await getStoredOrgVersion(module)) ?? null;
    } catch {
        return null;
    }
}

/** Đợi `sync-complete` message cho module từ BroadcastChannel. */
function waitForSyncComplete(module: OrganizationSyncModule, timeoutMs: number): Promise<boolean> {
    return new Promise((resolve) => {
        const broadcast = getOrgSyncBroadcast();
        const timer = setTimeout(() => {
            unsubscribe();
            resolve(false);
        }, timeoutMs);
        const unsubscribe = broadcast.subscribe((msg) => {
            if (msg.type === 'sync-complete' && msg.module === module) {
                clearTimeout(timer);
                unsubscribe();
                resolve(true);
            }
        });
    });
}

/**
 * Khởi tạo listener 1 lần khi app start.
 * Lắng nghe `sync-complete` từ tab writer khác → patch UI in-memory (không write IndexedDB,
 * vì IndexedDB đã được writer ghi — các tab khác tự thấy khi đọc).
 *
 * Gọi từ entry point (App.tsx hoặc login page) sau khi user authenticated.
 */
let _listenerInitialized = false;
export function initOrgSyncBroadcastListener(): void {
    if (_listenerInitialized) return;
    _listenerInitialized = true;
    const broadcast = getOrgSyncBroadcast();
    broadcast.subscribe((msg) => {
        if (msg.type === 'sync-complete') {
            // IndexedDB đã được writer ghi → các tab listener chỉ cần trigger UI reload.
            // dispatchOrganizationVersionUpdated phát window event 'indexedDBReloaded'
            // và 'updateOrganizationVersion' để hooks liên quan re-fetch.
            dispatchOrganizationVersionUpdated(msg.module);
        }
    });
}

// ========== Module-level batch (gom N SignalR events thành 1 API call) ==========
const _pendingBatchModules = new Set<OrganizationSyncModule>();
const MODULE_BATCH_MS = 300;

/**
 * Schedule a batched sync — gom N signals thành 1 call sau MODULE_BATCH_MS.
 * Dùng trong SignalR event handlers.
 */
export function scheduleBatchSync(module: OrganizationSyncModule): void {
    if (_pendingBatchModules.has(module)) return;
    _pendingBatchModules.add(module);
    setTimeout(async () => {
        _pendingBatchModules.delete(module);
        // Dùng coalescing để tránh duplicate calls
        try {
            await coalescingSync(module);
            dispatchOrganizationVersionUpdated(module);
        } catch (e) {
            console.error(`[scheduleBatchSync] ${module} failed`, e);
        }
    }, MODULE_BATCH_MS);
}

// ========== Version polling fallback (khi SignalR không khả dụng) ==========
let _versionPollInterval: ReturnType<typeof setInterval> | null = null;
const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 phút

export interface RemoteVersion {
    version: string;
    timestamp: string;
}

/**
 * Lấy version từ backend (lightweight GET, chỉ version number).
 * Fallback khi SignalR push thất bại.
 */
export async function getOrganizationRemoteVersion(module: OrganizationSyncModule): Promise<RemoteVersion | null> {
    try {
        const res = (await organizationApi.getVersion({
            module,
            PhanHe: getPhanHeFromStorage() || 'bsc',
        })) as unknown as RemoteVersion | null;
        if (!res?.version) return null;
        return { version: res.version, timestamp: res.timestamp };
    } catch {
        return null;
    }
}

/**
 * So sánh local version với remote version.
 * Nếu khác → trigger batch sync.
 */
export async function checkVersionMismatch(module: OrganizationSyncModule): Promise<void> {
    try {
        const localVersions = await getOrgVersions();
        const remote = await getOrganizationRemoteVersion(module);
        if (!remote) return;

        const localVer = localVersions?.[module as keyof OrgVersion] ?? null;
        if (localVer && localVer !== remote.version) {
            console.log(`[VersionMismatch] ${module}: local=${localVer} remote=${remote.version}`);
            scheduleBatchSync(module);
        }
    } catch {
        // Silent fail — version polling là backup, không critical
    }
}

/**
 * Bắt đầu periodic version polling.
 * Gọi khi SignalR reconnect hoặc khi app start.
 */
export function startVersionPolling(): void {
    if (_versionPollInterval) return;
    const modules: OrganizationSyncModule[] = ['employees', 'departments', 'positions', 'rest'];
    _versionPollInterval = setInterval(() => {
        modules.forEach((m) => checkVersionMismatch(m));
    }, VERSION_POLL_INTERVAL_MS);
}

/**
 * Dừng periodic version polling.
 */
export function stopVersionPolling(): void {
    if (_versionPollInterval) {
        clearInterval(_versionPollInterval);
        _versionPollInterval = null;
    }
}

type OrgApiBody = { success?: boolean; data?: string | null; message?: string; version?: OrgVersion | null };

async function saveOrgApiResponse(
    body: OrgApiBody | null | undefined,
    saveFn: (raw: any, ver?: OrgVersion | null) => Promise<boolean>
): Promise<boolean> {
    if (!body?.success || body?.data == null) return true;
    try {
        const raw = JSON.parse(CryptoService.decryptAES(body.data)) as any;
        return saveFn(raw, body.version ?? undefined);
    } catch (e) {
        console.error('[saveOrgApiResponse] failed', e);
        return false;
    }
}

async function syncOrganizationModuleFromApi(module: OrganizationSyncModule): Promise<boolean> {
    const version = await getOrgVersions();
    const body = { Version: version ?? undefined, PhanHe: 'bsc' as const };

    switch (module) {
        case 'employees':
            return saveOrgApiResponse(
                (await organizationApi.getEmployees(body)) as unknown as OrgApiBody,
                saveEmployeesToIndexedDB
            );
        case 'departments':
            return saveOrgApiResponse(
                (await organizationApi.getDepartments(body)) as unknown as OrgApiBody,
                saveDepartmentsToIndexedDB
            );
        case 'positions':
            return saveOrgApiResponse(
                (await organizationApi.getPositions(body)) as unknown as OrgApiBody,
                savePositionsToIndexedDB
            );
        case 'rest':
            return saveOrgApiResponse(
                (await organizationApi.getRest(body)) as unknown as OrgApiBody,
                saveRestToIndexedDB
            );
        default:
            return false;
    }
}

/**
 * Cập nhật organization từ API.
 * - Có module (employees | departments | positions | rest): chỉ gọi API tương ứng.
 * - Không có module: gọi 4 API song song (tương thích cũ).
 */
export async function updateOrganizationVersion(data?: any): Promise<boolean> {
    try {
        // eslint-disable-next-line @next/next/no-assign-module-variable
        const module = resolveOrganizationSyncModule(data);
        if (module) {
            const ok = await syncOrganizationModuleFromApi(module);
            dispatchOrganizationVersionUpdated(module);
            return ok;
        }

        const version = await getOrgVersions();
        const body = { Version: version ?? undefined, PhanHe: 'bsc' as const };
        const [resEmp, resDept, resPos, resRest] = (await Promise.all([
            organizationApi.getEmployees(body),
            organizationApi.getDepartments(body),
            organizationApi.getPositions(body),
            organizationApi.getRest(body),
        ])) as unknown as [OrgApiBody, OrgApiBody, OrgApiBody, OrgApiBody];

        await Promise.all([
            saveOrgApiResponse(resEmp, saveEmployeesToIndexedDB),
            saveOrgApiResponse(resDept, saveDepartmentsToIndexedDB),
            saveOrgApiResponse(resPos, savePositionsToIndexedDB),
            saveOrgApiResponse(resRest, saveRestToIndexedDB),
        ]);
        dispatchOrganizationVersionUpdated('all');
        return true;
    } catch (error) {
        console.error('Error updating organization version:', error);
        return false;
    }
}

/**
 * Xóa toàn bộ cache organization (4 key).
 */
export async function clearOrganizationCache(): Promise<boolean> {
    try {
        const { deleteRecord } = await import('@/services/indexedDB/indexed-db-service');
        await ensureDatabase();
        await Promise.all([
            deleteRecord('organization', ORG_KEY_EMPLOYEES),
            deleteRecord('organization', ORG_KEY_DEPARTMENTS),
            deleteRecord('organization', ORG_KEY_POSITIONS),
            deleteRecord('organization', ORG_KEY_REST),
        ]);
        _orgInitSyncedThisLoad = false;
        return true;
    } catch (error) {
        console.error('Error clearing organization cache:', error);
        return false;
    }
}

/**
 * Chuẩn hóa entity data từ API response về format HOA cho IndexedDB
 * Hỗ trợ cả department và position
 */
function normalizeEntityData(entity: any, entityType: OrganizationEntityType): any {
    const id = entity.ID ?? entity.id ?? '';
    const parentID = entity.ParentID ?? entity.parentID ?? '0';
    const ten = entity.Ten ?? entity.ten ?? '';
    const viTri = entity.ViTri ?? entity.viTri ?? 0;

    // Base fields cho cả department và position
    const baseEntity = {
        ID: id,
        Ten: ten,
        ParentID: parentID,
        ViTri: viTri,
    };

    // Department có thêm các fields đặc biệt
    if (entityType === 'departments') {
        const treePath = entity.TreePath ?? entity.treePath ?? '';
        const keyOrg = entity.Key ?? entity.key ?? 0;
        const prefix = entity.Prefix ?? entity.prefix ?? '';

        return {
            ...baseEntity,
            TreePath: treePath,
            Key: keyOrg,
            IsCompany: entity.IsCompany ?? entity.isCompany ?? keyOrg === 1 ? true : false,
            Prefix: prefix,
        };
    }

    // Position chỉ có base fields
    return baseEntity;
}

/**
 * Chuẩn hóa employee từ API / SignalR (camelCase hoặc HOA) sang OrgEmployee (PascalCase).
 */
function normalizeEmployeeEntity(emp: any): OrgEmployee {
    return {
        ID: emp.ID ?? emp.id ?? '',
        TenNhanVien: emp.TenNhanVien ?? emp.tenNhanVien ?? '',
        MaNhanVien: emp.MaNhanVien ?? emp.maNhanVien ?? '',
        BPID: emp.BPID ?? emp.bpid ?? '',
        CVID: emp.CVID ?? emp.cvid ?? '',
        Email: emp.Email ?? emp.email,
        Prefix: emp.Prefix ?? emp.prefix,
        Hinh: emp.Hinh ?? emp.hinh,
        GioiTinh: emp.GioiTinh ?? emp.gioiTinh,
        NghiViec: emp.NghiViec ?? emp.nghiViec ?? false,
        PhanHe: emp.PhanHe ?? emp.phanHe,
        IsQuanLy: emp.IsQuanLy ?? emp.isQuanLy ?? false,
        Parent: emp.Parent ?? emp.parent ?? '',
    };
}

/**
 * Chuẩn hóa period từ API / SignalR (camelCase hoặc HOA) sang OrgPeriod (PascalCase).
 */
function normalizePeriodEntity(p: any): OrgPeriod {
  return {
    ID: p.ID ?? p.id ?? '',
    Ten: p.Ten ?? p.ten,
    BatDau: p.BatDau ?? p.batDau,
    KetThuc: p.KetThuc ?? p.ketThuc,
    NamTaiChinh: p.NamTaiChinh ?? p.namTaiChinh,
    Site: p.Site ?? p.site,
    PeriodDetails: (Array.isArray(p.PeriodDetails ?? p.periodDetails)
      ? (p.PeriodDetails ?? p.periodDetails)
      : []
    ).map((pd: any): OrgPeriodDetail => ({
      ID: pd.ID ?? pd.id ?? '',
      NamBSCID: pd.NamBSCID ?? pd.namBSCID,
      Year: pd.Year ?? pd.year,
      BatDau: pd.BatDau ?? pd.batDau,
      KetThuc: pd.KetThuc ?? pd.ketThuc,
      Site: pd.Site ?? pd.site,
    })),
  };
}

/**
 * Cập nhật hoặc thêm 1 period vào IndexedDB (slice rest.period) từ dữ liệu SignalR / API.
 */
export async function updatePeriodInIndexedDB(periodData: any): Promise<boolean> {
  while (isSaving) await new Promise((r) => setTimeout(r, 10));
  isSaving = true;
  try {
    await ensureDatabase();
    const slice = await getRestSlice();
    if (!slice) return false;

    const norm = normalizePeriodEntity(periodData);
    const idStr = String(norm.ID);
    const arr = slice.period ?? [];
    const idx = arr.findIndex((e) => String(e.ID) === idStr);

    if (idx >= 0) arr[idx] = norm;
    else arr.push(norm);

    await putRecord('organization', {
      ...slice,
      period: arr,
      updatedAt: Date.now(),
    });
    return true;
  } catch (error) {
    console.error('Error updating period in IndexedDB:', error);
    return false;
  } finally {
    isSaving = false;
  }
}

/**
 * Xóa 1 period khỏi IndexedDB (slice rest.period) theo ID.
 */
export async function deletePeriodFromIndexedDB(periodId: string | number): Promise<boolean> {
  while (isSaving) await new Promise((r) => setTimeout(r, 10));
  isSaving = true;
  try {
    await ensureDatabase();
    const slice = await getRestSlice();
    if (!slice) return false;

    const idStr = String(periodId);
    const filtered = (slice.period ?? []).filter((e) => String(e.ID) !== idStr);

    await putRecord('organization', {
      ...slice,
      period: filtered,
      updatedAt: Date.now(),
    });
    return true;
  } catch (error) {
    console.error('Error deleting period from IndexedDB:', error);
    return false;
  } finally {
    isSaving = false;
  }
}

function normalizeUnitEntity(u: any): OrgUnit {
  const rawChilds = u?.Childs ?? u?.childs ?? [];
  return {
    ID: u?.ID ?? u?.id ?? '',
    Ten: u?.Ten ?? u?.ten ?? '',
    Childs: (Array.isArray(rawChilds) ? rawChilds : []).map((c: any) => {
      const ten = c?.Ten ?? c?.ten ?? '';
      const tenNgan = c?.tenNgan ?? c?.TenNgan ?? ten ?? c?.TenNgan ?? c?.ten ?? '';
      return {
        ...c,
        ID: c?.ID ?? c?.id ?? '',
        id: c?.id ?? c?.ID ?? '',
        Ten: c?.Ten ?? c?.ten ?? ten,
        ten: c?.ten ?? c?.Ten ?? ten,
        TenNgan: c?.TenNgan ?? c?.tenNgan ?? tenNgan,
        tenNgan: c?.tenNgan ?? c?.TenNgan ?? tenNgan,
        GiaTri: c?.GiaTri ?? c?.giaTri ?? 0,
        giaTri: c?.giaTri ?? c?.GiaTri ?? 0,
        Site: c?.Site ?? c?.site ?? '',
        site: c?.site ?? c?.Site ?? '',
        IDDoLuong: c?.IDDoLuong ?? c?.idDoLuong ?? '',
        idDoLuong: c?.idDoLuong ?? c?.IDDoLuong ?? '',
      };
    }),
    IsDefault: u?.IsDefault ?? u?.isDefault ?? false,
    hinhThucTinh: u?.hinhThucTinh ?? u?.HinhThucTinh ?? null,
  };
}

export async function updateUnitInIndexedDB(unitData: any): Promise<boolean> {
  while (isSaving) await new Promise((r) => setTimeout(r, 10));
  isSaving = true;
  try {
    await ensureDatabase();
    const slice = await getRestSlice();
    if (!slice) return false;

    const norm = normalizeUnitEntity(unitData);
    const idStr = String(norm.ID);
    if (!idStr) return false;

    const arr = [...(slice.units ?? [])];
    const idx = arr.findIndex((e) => String(e.ID) === idStr);
    if (idx >= 0) arr[idx] = norm;
    else arr.push(norm);

    await putRecord('organization', {
      ...slice,
      units: arr,
      updatedAt: Date.now(),
    });
    return true;
  } catch (error) {
    console.error('Error updating unit in IndexedDB:', error);
    return false;
  } finally {
    isSaving = false;
  }
}

export async function deleteUnitFromIndexedDB(unitId: string | number): Promise<boolean> {
  while (isSaving) await new Promise((r) => setTimeout(r, 10));
  isSaving = true;
  try {
    await ensureDatabase();
    const slice = await getRestSlice();
    if (!slice) return false;

    const idStr = String(unitId);
    const filtered = (slice.units ?? []).filter((e) => String(e.ID) !== idStr);

    await putRecord('organization', {
      ...slice,
      units: filtered,
      updatedAt: Date.now(),
    });
    return true;
  } catch (error) {
    console.error('Error deleting unit from IndexedDB:', error);
    return false;
  } finally {
    isSaving = false;
  }
}

/**
 * Cập nhật hoặc thêm 1 employee vào IndexedDB (slice employees) từ dữ liệu SignalR / API.
 */
export async function updateEmployeeInIndexedDB(employeeData: any): Promise<boolean> {
    while (isSaving) await new Promise((r) => setTimeout(r, 10));
    isSaving = true;
    try {
        await ensureDatabase();
        const slice = await getEmployeesSlice();
        if (!slice) return false;

        const norm = normalizeEmployeeEntity(employeeData);
        const idStr = String(norm.ID);
        const arr = slice.employees ?? [];
        const idx = arr.findIndex((e: any) => String(e.ID) === idStr);

        if (idx >= 0) arr[idx] = norm;
        else arr.push(norm);

        await putRecord('organization', {
            ...slice,
            employees: arr,
            updatedAt: Date.now(),
        });
        return true;
    } catch (error) {
        console.error('Error updating employee in IndexedDB:', error);
        return false;
    } finally {
        isSaving = false;
    }
}

/**
 * Chuẩn hóa role group từ SignalR / API sang OrgRole.
 */
function normalizeRoleGroupForIndexedDB(roleGroupData: any): OrgRole {
  return {
    ID: roleGroupData?.ID ?? roleGroupData?.id ?? '',
    Ten: roleGroupData?.Ten ?? roleGroupData?.ten ?? '',
    FullControl: roleGroupData?.FullControl ?? roleGroupData?.fullControl ?? false,
    QuanLy: roleGroupData?.QuanLy ?? roleGroupData?.quanLy ?? false,
    SiteModule: roleGroupData?.SiteModule ?? roleGroupData?.siteModule ?? 0,
  };
}

export async function updateRoleGroupInIndexedDB(roleGroupData: any): Promise<boolean> {
  while (isSaving) await new Promise((r) => setTimeout(r, 10));
  isSaving = true;
  try {
    await ensureDatabase();
    const slice = await getRestSlice();
    if (!slice) return false;

    const norm = normalizeRoleGroupForIndexedDB(roleGroupData);
    const idStr = String(norm.ID);
    if (!idStr) return false;

    const arr = [...(slice.roles ?? [])];
    const idx = arr.findIndex((r) => String(r.ID) === idStr);
    if (idx >= 0) arr[idx] = norm;
    else arr.push(norm);

    await putRecord('organization', {
      ...slice,
      roles: arr,
      updatedAt: Date.now(),
    });
    return true;
  } catch (error) {
    console.error('Error updating role group in IndexedDB:', error);
    return false;
  } finally {
    isSaving = false;
  }
}

export async function deleteRoleGroupFromIndexedDB(roleGroupId: string | number): Promise<boolean> {
  while (isSaving) await new Promise((r) => setTimeout(r, 10));
  isSaving = true;
  try {
    await ensureDatabase();
    const slice = await getRestSlice();
    if (!slice) return false;

    const idStr = String(roleGroupId);
    const filtered = (slice.roles ?? []).filter((r) => String(r.ID) !== idStr);

    await putRecord('organization', {
      ...slice,
      roles: filtered,
      updatedAt: Date.now(),
    });
    return true;
  } catch (error) {
    console.error('Error deleting role group from IndexedDB:', error);
    return false;
  } finally {
    isSaving = false;
  }
}

/**
 * Refetch profile user đang login (availablePhanHe / flag / listRole).
 * Không gọi getRest — org IndexedDB do SignalR/batch sync; getRest lỗi Version từng làm fail cả sync profile.
 */
export async function syncLoggedInUserPermissionsAfterRoleGroupChange(): Promise<void> {
  const { useAuthStore } = await import('@/store/useAuthStore');
  const { authApi, parseUserProfileResponse } = await import('@/services/authen');
  const { profile } = useAuthStore.getState();
  if (!profile?.id) return;

  try {
      const nextProfile = parseUserProfileResponse(
        await authApi.requestUserProfile(profile.id, getPhanHeFromStorage() ?? undefined),
      );
      if (nextProfile) {
        useAuthStore.getState().setProfile(nextProfile);
      }
      if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('permissionsUpdated', {
            detail: { timestamp: Date.now(), profileId: profile.id }
          }));
      }
  } catch (error) {
      console.error('[syncLoggedInUserPermissionsAfterRoleGroupChange]', error);
  }
}

export async function deleteEmployeeFromIndexedDB(employeeId: string | number): Promise<boolean> {
    while (isSaving) await new Promise((r) => setTimeout(r, 10));
    isSaving = true;
    try {
        await ensureDatabase();
        const slice = await getEmployeesSlice();
        if (!slice) return false;

        const idStr = String(employeeId);
        const filtered = (slice.employees ?? []).filter((e: any) => String(e.ID) !== idStr);

        await putRecord('organization', {
            ...slice,
            employees: filtered,
            updatedAt: Date.now(),
        });
        return true;
    } catch (error) {
        console.error('Error deleting employee from IndexedDB:', error);
        return false;
    } finally {
        isSaving = false;
    }
}

async function updateEntityInIndexedDB(
    entityData: any,
    entityType: OrganizationEntityType
): Promise<boolean> {
    while (isSaving) await new Promise((r) => setTimeout(r, 10));
    isSaving = true;
    try {
        const normalizedEntity = normalizeEntityData(entityData, entityType);
        const entityId = normalizedEntity.ID;

        if (entityType === 'departments') {
            const slice = await getDepartmentsSlice();
            if (!slice) return false;
            const arr = slice.departments;
            const idx = arr.findIndex((e: any) => String(e.ID) === String(entityId));
            if (idx >= 0) arr[idx] = normalizedEntity as OrgDepartment;
            else arr.push(normalizedEntity as OrgDepartment);
            await putRecord('organization', { ...slice, departments: arr, updatedAt: Date.now() });
            return true;
        }
        const slice = await getPositionsSlice();
        if (!slice) return false;
        const arr = slice.positions;
        const idx = arr.findIndex((e: any) => String(e.ID) === String(entityId));
        if (idx >= 0) arr[idx] = normalizedEntity as OrgPosition;
        else arr.push(normalizedEntity as OrgPosition);
        await putRecord('organization', { ...slice, positions: arr, updatedAt: Date.now() });
        return true;
    } catch (error) {
        console.error(`Error updating ${entityType} in IndexedDB:`, error);
        return false;
    } finally {
        isSaving = false;
    }
}

async function deleteEntityFromIndexedDB(
    entityId: string | number,
    entityType: OrganizationEntityType
): Promise<boolean> {
    while (isSaving) await new Promise((r) => setTimeout(r, 10));
    isSaving = true;
    try {
        const idStr = String(entityId);
        if (entityType === 'departments') {
            const slice = await getDepartmentsSlice();
            if (!slice) return false;
            const filtered = slice.departments.filter((e: any) => String(e.ID) !== idStr);
            await putRecord('organization', { ...slice, departments: filtered, updatedAt: Date.now() });
            return true;
        }
        const slice = await getPositionsSlice();
        if (!slice) return false;
        const filtered = slice.positions.filter((e: any) => String(e.ID) !== idStr);
        await putRecord('organization', { ...slice, positions: filtered, updatedAt: Date.now() });
        return true;
    } catch (error) {
        console.error(`Error deleting ${entityType} from IndexedDB:`, error);
        return false;
    } finally {
        isSaving = false;
    }
}

/**
 * Cập nhật hoặc thêm department vào IndexedDB sau khi create/update
 * @param departmentData - Department data từ API response (có thể là camelCase hoặc HOA)
 * @returns Promise<boolean>
 */
export async function updateDepartmentInIndexedDB(departmentData: any): Promise<boolean> {
    return updateEntityInIndexedDB(departmentData, 'departments');
}

/**
 * Xóa department khỏi IndexedDB sau khi delete
 * @param departmentId - ID của department cần xóa
 * @returns Promise<boolean>
 */
export async function deleteDepartmentFromIndexedDB(departmentId: string | number): Promise<boolean> {
    return deleteEntityFromIndexedDB(departmentId, 'departments');
}

/**
 * Cập nhật hoặc thêm position vào IndexedDB sau khi create/update
 * @param positionData - Position data từ API response (có thể là camelCase hoặc HOA)
 * @returns Promise<boolean>
 */
export async function updatePositionInIndexedDB(positionData: any): Promise<boolean> {
    return updateEntityInIndexedDB(positionData, 'positions');
}

/**
 * Xóa position khỏi IndexedDB sau khi delete
 * @param positionId - ID của position cần xóa
 * @returns Promise<boolean>
 */
export async function deletePositionFromIndexedDB(positionId: string | number): Promise<boolean> {
    return deleteEntityFromIndexedDB(positionId, 'positions');
}

/**
 * Gọi 4 API organization cùng lúc, decrypt và lưu từng slice có data.
 * Lưu ý: axios interceptor trả về response.data (body), không phải full response.
 * Đã có progress callbacks cho InitLoadingModal
 */
export async function fetchInitDataOrganization(): Promise<{
    success: boolean;
    message?: string;
}> {
    try {
        // Notify: bắt đầu load
        if (typeof window !== 'undefined') {
            (window as any).__initProgress?.setProgress?.({
                phase: 'critical',
                message: 'Đang kết nối server...',
                percent: 10,
            });
        }

        await ensureDatabase();
        const version = await getOrgVersions();
        const body = { Version: version ?? undefined, PhanHe: 'bsc' as const };

        // Notify: đang tải dữ liệu
        if (typeof window !== 'undefined') {
            (window as any).__initProgress?.setProgress?.({
                phase: 'critical',
                message: 'Đang tải dữ liệu cơ cấu tổ chức...',
                percent: 30,
            });
        }

        type OrgApiBody = { success?: boolean; data?: string | null; message?: string; version?: OrgVersion };
        const [resEmp, resDept, resPos, resRest] = (await Promise.all([
            organizationApi.getEmployees(body),
            organizationApi.getDepartments(body),
            organizationApi.getPositions(body),
            organizationApi.getRest(body),
        ])) as unknown as [OrgApiBody, OrgApiBody, OrgApiBody, OrgApiBody];

        const save = async (
            name: string,
            body: { success?: boolean; data?: string | null; version?: OrgVersion | null },
            saveFn: (raw: any, ver?: OrgVersion | null) => Promise<boolean>
        ) => {
            if (!body?.success || body?.data == null) {
                return true;
            }
            try {
                // Notify: đang xử lý
                if (typeof window !== 'undefined') {
                    (window as any).__initProgress?.setProgress?.({
                        phase: 'critical',
                        message: `Đang xử lý ${name}...`,
                        percent: 50,
                    });
                }

                const raw = JSON.parse(CryptoService.decryptAES(body.data!)) as any;
                const ok = await saveFn(raw, body.version ?? undefined);
                return ok;
            } catch (e) {
                console.error(`[fetchInitDataOrganization] ${name} failed`, e);
                return false;
            }
        };

        await Promise.all([
            save('employees', resEmp as any, saveEmployeesToIndexedDB),
            save('departments', resDept as any, saveDepartmentsToIndexedDB),
            save('positions', resPos as any, savePositionsToIndexedDB),
            save('rest', resRest as any, saveRestToIndexedDB),
        ]);

        // Notify: hoàn tất
        if (typeof window !== 'undefined') {
            (window as any).__initProgress?.setProgress?.({
                phase: 'done',
                message: 'Đã tải xong dữ liệu cơ cấu!',
                percent: 100,
            });
        }

        const anySuccess: boolean | undefined =
            resEmp?.success ?? resDept?.success ?? resPos?.success ?? resRest?.success;
        if (anySuccess === false) {
            return {
                success: false,
                message: (resEmp as any)?.message ?? (resRest as any)?.message ?? 'API error',
            };
        }
        _orgInitSyncedThisLoad = true;
        return { success: true };
    } catch (error) {
        console.error('[fetchInitDataOrganization] error', error);
        
        // Notify: lỗi
        if (typeof window !== 'undefined') {
            (window as any).__initProgress?.setProgress?.({
                phase: 'done',
                message: 'Đã xảy ra lỗi khi tải dữ liệu',
                percent: 100,
            });
        }
        
        return {
            success: false,
            message: error instanceof Error ? error.message : 'Unknown error',
        };
    }
}

/**
 * Load organization: 4 API song song (employees, departments, positions, rest) → IndexedDB.
 * @param _tenantID - Giữ cho tương thích, API mới không dùng
 * @param _languageID - Giữ cho tương thích
 */
export async function loadAndSaveOrganizationData(
    _tenantID: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _languageID: number = 1
): Promise<{ success: boolean; message?: string }> {
  return fetchInitDataOrganization();
}

/** Khởi tạo private-store (bộ phận + giai đoạn) sau login để trang đích load data ngay. */
export async function preparePrivateStoreAfterLogin(profile: any, tenantID: string): Promise<void> {
  const org = await getOrganizationIndexedDB();
  if (!org || !profile || !tenantID) return;

  const { usePrivateStore } = await import('@/store/usePrivateStore');
  const { mapAndSortPeriods } = await import('@/utils/periodUtils');
  const { getCurrentQuarterRangeString } = await import('@/helper');

  const allDeps: any[] = Array.isArray(org.departments)
    ? org.departments
    : Object.values(org.departments || {});

  const {
    setTreeSelectDepartment,
    setDepartmentId,
    setGiaiDoan,
    setDateChoiceDefault,
    initRequestHeader,
    setPhanHe,
  } = usePrivateStore.getState();

  setPhanHe('bsc');

  const isAdmin = profile?.isAdmin;
  const isQuanLy = profile?.isQuanLy;
  const isNhanVien = !isQuanLy && !isAdmin;

  if (isAdmin) {
    const root = allDeps.find(
      (d: any) => String(d.ParentID ?? d.parentID ?? '0') === '0' || d.Key === 1
    );
    if (root) {
      const idSelect = String(root.ID ?? root.id ?? '');
      setDepartmentId(idSelect);
      setTreeSelectDepartment({
        selectedValue: { value: idSelect, label: String(root.Ten ?? root.ten ?? '') },
        tenantID,
        idSelect,
        isEmployee: false,
        parentID: String(root.ParentID ?? root.parentID ?? '0'),
        isCompany: root.Key === 1,
      });
    }
  } else if (isNhanVien && profile?.id && profile?.boPhanId) {
    const bpOri = allDeps.find((d: any) => String(d.ID ?? d.id) === String(profile.boPhanId));
    const empId = String(profile.id);
    setDepartmentId(empId);
    setTreeSelectDepartment({
      selectedValue: {
        value: `emp_${empId}_dep_${profile.boPhanId}`,
        label: profile.tenNhanVien || '',
      },
      tenantID,
      idSelect: empId,
      isEmployee: true,
      parentID: String(bpOri?.ParentID ?? bpOri?.parentID ?? '0'),
      isCompany: false,
    });
  } else if (profile?.boPhanId) {
    const bpOri = allDeps.find((d: any) => String(d.ID ?? d.id) === String(profile.boPhanId));
    if (bpOri) {
      const idSelect = String(bpOri.ID ?? bpOri.id ?? '');
      setDepartmentId(idSelect);
      setTreeSelectDepartment({
        selectedValue: { value: idSelect, label: String(bpOri.Ten ?? bpOri.ten ?? '') },
        tenantID,
        idSelect,
        isEmployee: false,
        parentID: String(bpOri.ParentID ?? bpOri.parentID ?? '0'),
        isCompany: bpOri.Key === 1,
      });
    }
  }

  const rawPeriods = org.period || [];
  if (rawPeriods.length > 0) {
    const periods = mapAndSortPeriods(rawPeriods as any);
    const currentYear = new Date().getFullYear();
    const convertedData = periods.map((period) => {
      const years: string[] = [];
      period.periodDetails?.forEach((detail) => years.push(String(detail.year)));
      return {
        idGiaiDoan: period.id,
        years: years.sort(),
        namTaiChinh: period.namTaiChinh || false,
        startDate: period.batDau || null,
        endDate: period.ketThuc || null,
      };
    });

    let selectedYear = currentYear;
    let foundStage = convertedData.find((stage) => stage.years.includes(String(selectedYear)));
    if (!foundStage) {
      const allYears = convertedData.flatMap((stage) => stage.years.map((y) => parseInt(y, 10)));
      if (allYears.length > 0) {
        const nearestYear = allYears.reduce((closest, year) => {
          const dClosest = Math.abs(closest - currentYear);
          const dYear = Math.abs(year - currentYear);
          if (dYear === dClosest) return year < closest ? year : closest;
          return dYear < dClosest ? year : closest;
        }, allYears[0]);
        selectedYear = nearestYear;
        foundStage = convertedData.find((stage) => stage.years.includes(String(selectedYear)));
      }
    }

    if (foundStage) {
      const idGiaiDoan = foundStage.idGiaiDoan.toString();
      const dateRange = getCurrentQuarterRangeString(
        selectedYear,
        foundStage.namTaiChinh,
        foundStage.startDate ? new Date(foundStage.startDate).getMonth() + 1 : 0
      );
      setGiaiDoan(
        idGiaiDoan,
        selectedYear,
        dateRange,
        foundStage.namTaiChinh,
        foundStage.startDate,
        foundStage.endDate
      );
      setDateChoiceDefault(dateRange);
    }
  }

  initRequestHeader();
}
